import React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { NewTrash, Reload2 } from '@crehana/icons';

import { useTranslation } from '@crehana/i18n';
import { Tooltip } from '@crehana/ui';
import { useProductsContainer } from '@crehana/web/dist/ReactContexts/Products';

import LazyImageV2 from 'Shared/LazyImageV2';

import { SHOPPING_CART_TRANSLATION_KEY } from 'Jsx/Utils/transformKeys';

export const Rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const CloseButton = styled.button<{
  loadingRemove: boolean;
}>`
  display: block;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    outline: none;
    right: -16px;
    top: 16px;
    cursor: pointer;
    transition: transform 0.2s ease-in-out;
    &:hover {
      transform: scale(1.3);
    }

    ${props =>
      props.loadingRemove &&
      css`
        & svg {
          opacity: 0.8;
          transform: scale(1.2);
          animation: ${Rotate} 0.5s linear forwards infinite;
        }
      `}
    ${props =>
      props.loadingRemove &&
      css`
        cursor: progress;
      `}
  }
`;

const Image = styled.div`
  margin-bottom: 24px;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  background-color: #4b22f4;

  & > div {
    height: 0 !important;
    padding-bottom: 100%;
  }

  @media (min-width: 768px) {
    width: 92px;
    height: 66px;
    margin-bottom: 0;
    & > div {
      padding-bottom: 60%;
      width: 92px !important;
      height: 66px !important;
    }
  }
`;

const CartItem = ({ ...props }) => {
  const {
    item: {
      originalId,
      title,
      image,
      type,
      url,
      extraDiscount,
      prices: { currencySymbol, price, realPrice },
    },
    setLoadingRemove,
    loadingRemove,
  } = props;

  const { removeBundleFromCart, removeCourseFromCart } = useProductsContainer();
  const { t } = useTranslation(SHOPPING_CART_TRANSLATION_KEY);

  return (
    <div className="pt-16 pb-16" data-cy="cartItem">
      <div className="flex">
        <Image>
          <LazyImageV2
            lazyUrl={image}
            imgxParams={{ w: 92, h: 66 }}
            alt="course"
          />
        </Image>
        <div className="flex flex-col items-start justify-start w-full ml-16 sm:ml-24">
          {/* Title */}
          <div className="flex items-center justify-between w-full">
            <div className="mb-8">
              <a
                className="block font-subtitle3 text-base-main"
                href={url}
                style={{ maxWidth: '500px' }}
              >
                {title}
              </a>
            </div>
            <div className="flex">
              <Tooltip
                label={t('DELETE', { defaultValue: 'Eliminar' })}
                position="left"
              >
                <CloseButton
                  onClick={() => {
                    setLoadingRemove(originalId);
                    if (type === 'COURSE') {
                      removeCourseFromCart({ courseId: originalId });
                    } else {
                      removeBundleFromCart({ bundleId: originalId });
                    }
                  }}
                  loadingRemove={loadingRemove}
                >
                  {loadingRemove ? (
                    <Reload2 color="rgba(102, 102, 102, 0.5)" size={20} />
                  ) : (
                    <NewTrash color="rgba(102, 102, 102, 0.5)" size={20} />
                  )}
                </CloseButton>
              </Tooltip>
            </div>
          </div>

          {/* Price */}
          <div className="flex flex-wrap items-baseline">
            <span className="tw-font-semibold text-red-main tw-font-pop text-16 leading-24">
              {currencySymbol}
              {price.toLocaleString('en-US')}
            </span>
            {realPrice && realPrice > price && (
              <span className="text-gray-dark font-discount">
                {currencySymbol} {realPrice.toLocaleString('en-US')}
              </span>
            )}
            {extraDiscount > 0 && (
              <span className="ml-8 text-gray-dark tw-font-pop text-14 leading-20">
                + {extraDiscount}% Dto
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
